(function (factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  if (!customElements.get('pm-cart-recs')) api.mount();
})(function () {
  var rawCache = new Map();
  var inflight = new Map();

  // The Add button to give focus back to when a failed Add's row was replaced by the repaint of the resync
  // that follows it: { variantId, until }. Taken by the row that draws that button (refocusAdd).
  var pendingRefocus = null;

  // Thumbnail request width. The well is 4:5 and about 110 to 125 CSS px wide,
  // so 320 covers a 2x screen. The img width/height attributes describe the
  // 4:5 well the image fills, not the photo's own ratio.
  var IMAGE_WIDTH = 320;
  var IMAGE_HEIGHT = 400;

  // How long an Add waits for /cart/add before giving up on it (as assets/product-form.js).
  var ADD_TIMEOUT = 15000;

  // After /cart/add answers, how much longer the repaint waits for the recommendations that were started at the
  // tap (the added product leads the cart, so they are the row's next list). Past it the row arrives when they do.
  // The wait is spent under the cart lock (addVariant), so no other cart request can start in it.
  var PREFETCH_WAIT = 500;

  // A list fetched this recently counts as fresh even where a fresh one was asked for (a repaint re-mounts the
  // row, and the prefetch has just fetched it).
  var FRESH_MS = 10000;

  function formatMoney(cents, format) {
    var template = String(format || '');
    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return (Number(cents) / 100).toFixed(2);

    var amount = Number(cents);
    var withDelimiters = function (precision, thousands, decimal) {
      var fixed = (amount / 100).toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return precision ? dollars + decimal + parts[1] : dollars;
    };

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(0, ' ', '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(2, ' ', '.');
        break;
      default:
        value = withDelimiters(2, ',', '.');
    }

    return template.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function imageSrc(image) {
    if (!image) return '';
    var raw = typeof image === 'string' ? image : image.src || '';
    if (!raw) return '';
    var absolute = raw.indexOf('//') === 0 ? 'https:' + raw : raw;
    try {
      var base = typeof window !== 'undefined' && window.location ? window.location.origin : 'https://example.com';
      var url = new URL(absolute, base);
      url.searchParams.set('width', String(IMAGE_WIDTH));
      return url.toString();
    } catch (error) {
      return absolute;
    }
  }

  // Text before the first " \u2014 " (space, em dash, space), trimmed. Falls back to the
  // full title when that text is empty. Mirrors snippets/pm-product-name.liquid.
  function shortName(title) {
    var full = String(title || '').trim();
    var head = full.split(' \u2014 ')[0].trim();
    return head || full;
  }

  function hasTag(product, tag) {
    var tags = product && product.tags;
    if (!tags) return false;
    var list = Array.isArray(tags) ? tags : String(tags).split(',');
    for (var i = 0; i < list.length; i += 1) {
      if (String(list[i]).trim() === tag) return true;
    }
    return false;
  }

  // Which photo a card shows. Alt text steers it: #main leads, #nocard keeps a photo off the card, #hide
  // drops it everywhere; otherwise the featured image. Only image media count (a video poster is not a photo).
  function cardImage(product) {
    var media = product && Array.isArray(product.media) ? product.media : [];
    var main = null;
    var plain = null;
    for (var i = 0; i < media.length; i += 1) {
      var item = media[i];
      if (!item || item.media_type !== 'image' || !item.preview_image || !item.preview_image.src) continue;
      var alt = String(item.alt || '');
      if (alt.indexOf('#hide') !== -1) continue;
      if (!main && alt.indexOf('#main') !== -1) main = item;
      if (!plain && alt.indexOf('#nocard') === -1) plain = item;
    }
    var picked = main || plain;
    if (picked) return picked.preview_image;
    // Media listed but nothing eligible (all #hide or #nocard): no photo, an empty well like a product without
    // one. The featured image is only for a response that carries no media list.
    if (media.length) return null;
    return product.featured_image || (product.images && product.images[0]);
  }

  // The drawer's "Added to cart" line and its announcement (assets/cart-drawer.js showAdded, announceAdded).
  // True when the drawer announced it, so settleAfterAdd leaves its own sentence out. /cart has no drawer.
  function showAddedLine(title) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.showAdded !== 'function') return false;
    drawer.showAdded(title);
    return typeof drawer.announceAdded === 'function' && drawer.announceAdded();
  }

  // POSTs /cart/add and resolves { ok, status, data } for a JSON object. Rejects on a timeout, a network
  // error and any other answer, so none of those can reach the shopper as raw error text.
  function postAdd(url, init) {
    var controller = new AbortController();
    var timer = setTimeout(function () {
      controller.abort();
    }, ADD_TIMEOUT);
    init.signal = controller.signal;
    return fetch(url, init)
      .then(function (response) {
        return response.json().then(function (data) {
          if (!data || typeof data !== 'object') throw new Error('/cart/add did not answer with a JSON object');
          return { ok: response.ok, status: response.status, data: data };
        });
      })
      .finally(function () {
        clearTimeout(timer);
      });
  }

  function availableVariant(product) {
    var variants = product && Array.isArray(product.variants) ? product.variants : [];
    for (var i = 0; i < variants.length; i += 1) {
      var variant = variants[i];
      if (variant && variant.available && !variant.requires_selling_plan) return variant;
    }
    return null;
  }

  // A product with more than one variant cannot be added blind: the shopper has to
  // see which size or colour they get, so its card links to the product page instead.
  function hasChoices(product) {
    return Boolean(product && Array.isArray(product.variants) && product.variants.length > 1);
  }

  // The option lines the real cart line shows for the variant ("Color: Gray", as snippets/cart-drawer.liquid):
  // none for a product with only the default Title option. The product and variant JSON carry them already.
  function optionLines(product, variant) {
    var options = product && Array.isArray(product.options) ? product.options : [];
    var values = variant && Array.isArray(variant.options) ? variant.options : [];
    var lines = [];
    options.forEach(function (option, index) {
      var name = typeof option === 'string' ? option : option && option.name;
      var value = values[index];
      if (name && value) lines.push({ name: String(name), value: String(value) });
    });
    if (lines.length === 1 && lines[0].name === 'Title' && lines[0].value === 'Default Title') return [];
    return lines;
  }

  function filterList(products, cartIds) {
    var cards = [];
    var list = Array.isArray(products) ? products : [];
    for (var i = 0; i < list.length; i += 1) {
      var product = list[i];
      if (!product || product.available === false) continue;
      if (cartIds.has(String(product.id))) continue;
      var variant = availableVariant(product);
      if (!variant) continue;
      cards.push({ product: product, variant: variant });
      if (cards.length === 3) break;
    }
    return cards;
  }

  function chooseCards(pair, cartIds) {
    var source = pair || {};
    var complementary = filterList(source.complementary, cartIds);
    var picked = complementary.length ? complementary : filterList(source.related, cartIds);
    return picked.slice(0, 3);
  }

  function idSet(value) {
    return new Set(
      String(value || '')
        .split(',')
        .map(function (id) {
          return id.trim();
        })
        .filter(Boolean)
    );
  }

  function intentUrl(base, productId, intent) {
    var url = new URL(base, window.location.origin);
    url.searchParams.set('product_id', String(productId));
    url.searchParams.set('limit', '6');
    url.searchParams.set('intent', intent);
    return url.toString();
  }

  function fetchIntent(base, productId, intent) {
    return fetch(intentUrl(base, productId, intent), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (data) {
        if (!data) return null;
        return Array.isArray(data.products) ? data.products : [];
      })
      .catch(function () {
        return null;
      });
  }

  function loadRecommendations(base, productId, fresh) {
    var key = String(productId);
    if (inflight.has(key)) return inflight.get(key);
    var kept = rawCache.get(key);
    if (kept && (!fresh || Date.now() - kept.at < FRESH_MS)) return Promise.resolve(kept);

    var job = Promise.all([
      fetchIntent(base, key, 'complementary'),
      fetchIntent(base, key, 'related'),
    ]).then(function (lists) {
      if (inflight.get(key) === job) inflight.delete(key);
      if (lists[0] === null && lists[1] === null) {
        throw new Error('recommendations failed');
      }
      var value = {
        complementary: lists[0] || [],
        related: lists[1] || [],
        at: Date.now(),
      };
      rawCache.set(key, value);
      return value;
    });

    inflight.set(key, job);
    return job;
  }

  function element(tag, className, text) {
    var node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // Whether an /cart/add answer carries every section asked for but the cart icon (those draw the lines and the
  // totals). An answer without them cannot show the add, however OK it is.
  function hasSections(sections, wanted) {
    if (!sections || typeof sections !== 'object') return false;
    return wanted.every(function (id) {
      return id === 'cart-icon-bubble' || (typeof sections[id] === 'string' && sections[id] !== '');
    });
  }

  function sectionInner(html, selector) {
    if (!html) return '';
    var node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  }

  function cartProductIds(items) {
    var ids = [];
    (items || []).forEach(function (item) {
      var id = String(item.product_id);
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function mount() {
    class PmCartRecs extends HTMLElement {
      connectedCallback() {
        this.token = 0;
        this.loading = false;
        this.renderedSig = '';
        this.onCart = function (event) {
          var items = event && event.cartData && event.cartData.items;
          if (!Array.isArray(items)) return;
          this.applyItems(items);
        }.bind(this);
        this.bindCart();
        this.refresh(true);
      }

      disconnectedCallback() {
        this.token += 1;
        if (this.unsub) this.unsub();
        this.unsub = null;
      }

      bindCart() {
        var attach = function () {
          if (!this.isConnected || this.unsub || typeof subscribe !== 'function') return;
          this.unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCart);
        }.bind(this);

        if (typeof subscribe === 'function') {
          attach();
          return;
        }

        document.addEventListener('DOMContentLoaded', attach, { once: true });
      }

      applyItems(items) {
        if (!items.length) {
          this.dataset.productId = '';
          this.dataset.cartIds = '';
          this.hide();
          return;
        }

        var productId = String(items[0].product_id);
        var cartIds = cartProductIds(items).join(',');
        if (
          productId === this.dataset.productId &&
          cartIds === this.dataset.cartIds &&
          (this.renderedSig || this.loading)
        ) {
          return;
        }

        this.dataset.productId = productId;
        this.dataset.cartIds = cartIds;
        this.refresh(true);
      }

      refresh(fresh) {
        var productId = this.dataset.productId;
        if (!productId) {
          this.hide();
          return Promise.resolve();
        }

        var token = (this.token += 1);
        var cartIds = idSet(this.dataset.cartIds);

        // Another product leads the cart: the cards on show stay until its list can replace them (at once when it
        // is cached), so the row never goes blank in between.
        var cached = rawCache.get(String(productId));
        if (cached) this.showCards(chooseCards(cached, cartIds), productId);

        this.loading = true;
        return loadRecommendations(this.dataset.url, productId, fresh)
          .then(function (pair) {
            if (!this.isConnected || token !== this.token) return;
            this.showCards(chooseCards(pair, cartIds), productId);
          }.bind(this))
          .catch(function () {
            if (!this.isConnected || token !== this.token) return;
            if (!this.renderedSig) this.hide();
          }.bind(this))
          .finally(function () {
            if (token === this.token) this.loading = false;
          }.bind(this));
      }

      showCards(cards, productId) {
        this.removeAttribute('data-pm-pending');
        if (!cards.length) {
          this.hide();
          return;
        }

        var sig = cards
          .map(function (card) {
            return card.product.id + ':' + card.variant.id + ':' + card.variant.price;
          })
          .join('|');

        if (sig === this.renderedSig && !this.hasAttribute('hidden')) return;
        this.render(cards);
        this.renderedSig = sig;
        this.removeAttribute('hidden');
        this.refocusAdd();
      }

      hide() {
        this.removeAttribute('data-pm-pending');
        this.setAttribute('hidden', '');
        this.renderedSig = '';
        var list = this.querySelector('[data-pm-recs-list]');
        if (list) list.replaceChildren();
        this.clearStatus();
      }

      // Back to the visually hidden live region it is for successes, text cleared.
      clearStatus() {
        var status = this.querySelector('[data-pm-recs-status]');
        if (!status) return;
        status.textContent = '';
        status.classList.add('visually-hidden');
      }

      render(cards) {
        var list = this.querySelector('[data-pm-recs-list]');
        if (!list) return;

        var format = this.dataset.moneyFormat;
        var addLabel = this.dataset.addLabel || 'Add';
        var addFull = this.dataset.addFull || addLabel;
        var fragment = document.createDocumentFragment();

        cards.forEach(function (card) {
          var product = card.product;
          var variant = card.variant;
          var item = document.createElement('li');
          item.className = 'pm-cart-recs__card';

          var media = document.createElement('a');
          media.className = 'pm-cart-recs__media';
          if (hasTag(product, 'img-cover')) media.classList.add('pm-well--cover');
          media.href = product.url;
          media.tabIndex = -1;
          media.setAttribute('aria-hidden', 'true');
          var source = imageSrc(cardImage(product));
          if (source) {
            var img = document.createElement('img');
            img.src = source;
            img.alt = '';
            img.width = IMAGE_WIDTH;
            img.height = IMAGE_HEIGHT;
            img.loading = 'lazy';
            img.decoding = 'async';
            media.append(img);
          }
          item.append(media);

          var copy = document.createElement('div');
          copy.className = 'pm-cart-recs__copy';
          var title = document.createElement('p');
          title.className = 'pm-cart-recs__title';
          var titleLink = document.createElement('a');
          titleLink.href = product.url;
          titleLink.textContent = shortName(product.title);
          title.append(titleLink);
          var price = document.createElement('p');
          price.className = 'pm-cart-recs__price';
          price.textContent = formatMoney(variant.price, format);
          copy.append(title, price);
          item.append(copy);

          var control;
          if (hasChoices(product)) {
            // Same class as the Add button, so the two are styled identically.
            control = document.createElement('a');
            control.className = 'pm-cart-recs__add pm-cart-recs__choose';
            control.href = product.url;
            control.textContent = this.dataset.chooseLabel || 'Choose';
            control.setAttribute(
              'aria-label',
              (this.dataset.chooseFull || 'Choose options for __PRODUCT__').replace('__PRODUCT__', function () { return product.title; })
            );
          } else {
            control = document.createElement('button');
            control.type = 'button';
            control.className = 'pm-cart-recs__add';
            control.dataset.variantId = String(variant.id);
            control.textContent = addLabel;
            var variantName =
              variant.public_title || (variant.title && variant.title !== 'Default Title' ? variant.title : '');
            control.setAttribute(
              'aria-label',
              variantName ? addFull + ': ' + product.title + ', ' + variantName : addFull + ': ' + product.title
            );
            control.addEventListener(
              'click',
              this.addVariant.bind(
                this,
                variant.id,
                control,
                product.id,
                shortName(product.title),
                product.title,
                optionLines(product, variant)
              )
            );
          }
          item.append(control);
          fragment.append(item);
        }, this);

        list.replaceChildren(fragment);
        // a row that arrives while a cart request is pending (assets/cart.js) is locked like the rest
        if (window.pmCartLock) window.pmCartLock.apply();
      }

      // The product is in the cart as soon as /cart/add answers, and being the newest line it leads the cart (so
      // its list is the row's next one, already fetched at the tap). Re-anchor the row on it now instead of after
      // the follow-up cart.js round trip: its card drops out and the row swaps to the new list in one go.
      noteAdded(productId) {
        if (productId === undefined || productId === null || productId === '') return;
        var added = String(productId);
        var ids = idSet(this.dataset.cartIds);
        ids.delete(added);
        this.dataset.cartIds = [added].concat(Array.from(ids)).join(',');
        this.dataset.productId = added;
        this.refresh(false);
      }

      addVariant(variantId, button, productId, name, title, options) {
        var lock = window.pmCartLock;
        // The retry button of a failed Add is enabled again while the resync that follows still holds the lock (so it
        // can take focus), but cannot start an Add before the resync is done
        if (button.disabled || (lock && lock.held)) return;
        var openDrawer = Boolean(this.closest('cart-drawer'));
        var restoreButton = null;
        var pending = null;
        var request;
        // set once the add's answer is drawn: a failure after that is the follow-up question, not a failed Add
        var painted = false;
        var wanted = [];
        // The cart is locked (assets/cart.js) from the tap until the repaint, or the handling of a failure (up to
        // the repaint of its resync), has finished: no quantity change, Remove, Checkout or second Add can start in
        // between. Released once, by whichever comes first.
        var held = false;
        var release = function () {
          if (!held) return;
          held = false;
          if (lock) lock.unlock();
        };

        // whatever throws before the request exists must not leave the cart locked
        try {
          restoreButton = this.markBusy(button, title);
          if (lock) {
            lock.lock();
            held = true;
          }
          // the line the add is about to create, until the repaint brings the real one
          pending = this.insertPending(button, openDrawer, options);
          // The added product leads the cart, so its recommendations are the row's next list: start them now
          var prefetch = loadRecommendations(this.dataset.url, productId, false).catch(function () {});
          // the cart page has no drawer to repaint
          var sections = document.getElementById('CartDrawer')
            ? ['cart-drawer', 'cart-icon-bubble']
            : ['cart-icon-bubble'];
          var itemsRoot = document.getElementById('main-cart-items');
          var footer = document.getElementById('main-cart-footer');
          if (itemsRoot && itemsRoot.dataset.id) sections.push(itemsRoot.dataset.id);
          if (footer && footer.dataset.id) sections.push(footer.dataset.id);
          wanted = sections;

          var routes = window.routes || {};
          // a failure line from the last Add goes away; emptied first so a repeated failure is announced again
          this.clearStatus();
          request = postAdd(routes.cart_add_url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          });
        } catch (error) {
          release();
          if (pending) pending.remove();
          if (restoreButton) restoreButton();
          throw error;
        }

        request
          .then(function (result) {
            if (!result.ok || result.data.status) {
              var refused = new Error(result.data.description || result.data.message || this.dataset.error || '');
              refused.shopperText = result.status === 422 || Number(result.data.status) === 422;
              throw refused;
            }
            // OK, but nothing to draw it with: the add may well have landed, and the resync says what the cart holds
            if (!hasSections(result.data.sections, wanted)) {
              throw new Error('/cart/add answered without the sections of the cart');
            }
            // the repainted row paints from the cache, not blank (it is usually there long before the add answers)
            return Promise.race([prefetch, wait(PREFETCH_WAIT)]).then(function () {
              return result;
            });
          }.bind(this))
          .then(function (result) {
            try {
              this.noteAdded(result.data.product_id !== undefined ? result.data.product_id : productId);
              this.paintSections(result.data.sections, openDrawer);
              painted = true;
              if (pending) pending.remove();
              var announced = openDrawer && showAddedLine(result.data.product_title || name);
              this.settleAfterAdd(name, openDrawer, announced);
              // the card of this Add can have outlived the repaint (the row did not change): it is not busy any more
              if (button.isConnected) restoreButton();
            } finally {
              // the repaint drew fresh controls; this frees the ones it did not reach (Checkout of the cart page).
              // Not before the resync of a failure: a repaint that threw is one.
              if (painted) release();
            }
            return this.fetchCart();
          }.bind(this))
          .then(this.shareCart.bind(this, variantId))
          .catch(function (error) {
            if (pending) pending.remove();
            if (painted) {
              release();
              if (!button.isConnected) return;
              restoreButton();
              button.focus({ preventScroll: true });
              this.showAddFailure(error && error.shopperText ? error.message : null);
              return;
            }
            // the lock stays until the resync has been drawn: recoverFromFailure releases it then, before the
            // follow-up read; the finally is for a throw before that
            return Promise.resolve()
              .then(function () {
                return this.recoverFromFailure(button, restoreButton, variantId, error, release);
              }.bind(this))
              .finally(release);
          }.bind(this));
      }

      // The cart as the server has it now. A read the server refuses (not OK) is a failed read. With `limit` (ms) a
      // read that takes longer is given up.
      fetchCart(limit) {
        var controller = limit ? new AbortController() : null;
        var timer = controller ? setTimeout(function () { controller.abort(); }, limit) : 0;
        var options = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
        if (controller) options.signal = controller.signal;
        return fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js', options)
          .then(function (response) {
            if (!response.ok) throw new Error('/cart.js answered ' + response.status);
            return response.json();
          })
          .finally(function () {
            clearTimeout(timer);
          });
      }

      // Tells the page what the cart holds and the row to offer other products accordingly. The step after an Add that
      // went through, and after the resync of one that did not. An answer without a list of lines is not a cart:
      // nothing is told (a subscriber reads cartData.items).
      shareCart(variantId, cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: cart,
            variantId: String(variantId),
          });
        }
        if (this.isConnected) this.applyItems(cart.items);
      }

      // After the resync of a failed Add, which may have landed all the same: the row stops offering a product that is
      // in the cart now. Runs once the lock is released, so it is bounded by its own timeout (the resync's). A cart
      // that cannot be read, in time or at all, changes nothing and says nothing.
      syncRow(variantId) {
        var limit = typeof CART_RESYNC_TIMEOUT === 'number' ? CART_RESYNC_TIMEOUT : 5000;
        return this.fetchCart(limit)
          .then(this.shareCart.bind(this, variantId))
          .catch(function () {});
      }

      // An Add that did not go through: the button is usable again, the shopper is told, and then the server is
      // asked what the cart holds, because the add may have landed (an answer that was lost, or one without
      // sections) and the page must show what the server has (the same question a failed quantity change ends in,
      // assets/cart.js). In that order: the resync, whose repaint replaces the row, then the message and the focus put
      // back on the row there now, then the lock released (`release`), and only then the follow-up read of the cart
      // for the row (syncRow), which therefore holds nothing up. Always resolves.
      recoverFromFailure(button, restoreButton, variantId, error, release) {
        var text = error && error.shopperText ? error.message : null;
        var focused = false;
        if (button.isConnected) {
          restoreButton();
          button.focus({ preventScroll: true });
          focused = true;
        }
        this.showAddFailure(text);

        // the resync draws the drawer where the shopper had it scrolled to (CartItems.keepingDrawerScroll)
        var resynced = false;
        var asked = window.pmCartResync
          ? window.pmCartResync().then(function () {
              resynced = true;
            })
          : Promise.resolve();
        return asked
          .catch(function () {
            // the server cannot be asked: the page stays as it is, with the message
          })
          .then(function () {
            // the drawer's repaint replaced this row, message and button included: both are put back on the
            // row there now
            var row = this.isConnected ? this : document.querySelector('cart-drawer pm-cart-recs');
            if (row && row !== this) {
              row.showAddFailure(text);
              if (focused && !button.isConnected) pendingRefocus = { variantId: String(variantId), until: Date.now() + 5000 };
            }
            release();
            // the unlock enabled the buttons of the row the repaint drew: only now can one take the focus
            row = this.isConnected ? this : document.querySelector('cart-drawer pm-cart-recs');
            if (row) row.refocusAdd();
            return resynced ? this.syncRow(variantId) : undefined;
          }.bind(this));
      }

      // After a failed Add whose row was replaced: the Add button of the same product gets the focus back, if
      // the shopper has not put it anywhere of their own since (the drawer's heading or dialog is where the repaint
      // left it). Called whenever a row draws its cards; does nothing without a refocus waiting.
      refocusAdd() {
        if (!pendingRefocus) return;
        if (Date.now() > pendingRefocus.until) {
          pendingRefocus = null;
          return;
        }
        var again = this.querySelector('button.pm-cart-recs__add[data-variant-id="' + pendingRefocus.variantId + '"]');
        if (!again || again.disabled || !again.getClientRects().length) return;
        pendingRefocus = null;
        var active = document.activeElement;
        if (active && active !== document.body && !active.matches('.drawer__heading, .drawer__inner')) return;
        again.focus({ preventScroll: true });
      }

      // The tapped button reads "Adding..." with a spinner and is disabled and aria-busy until the add is
      // settled; its accessible name says the same ("Adding <product>"). Returns the function that puts the
      // label, the name and the state back.
      markBusy(button, title) {
        var label = document.createDocumentFragment();
        while (button.firstChild) label.append(button.firstChild);
        var name = button.getAttribute('aria-label');
        var restored = false;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (title) button.setAttribute('aria-label', 'Adding ' + title);
        button.append(this.addingContent());
        return function () {
          // once: the label nodes are moved back, a second call would find the fragment empty
          if (restored) return;
          restored = true;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (name === null) button.removeAttribute('aria-label');
          else button.setAttribute('aria-label', name);
          button.replaceChildren(label);
        };
      }

      // A spinner (the loading-spinner asset, kept in a template by the snippet) and the "Adding..." word
      addingContent() {
        var fragment = document.createDocumentFragment();
        var spinner = this.querySelector('template[data-pm-recs-spinner]');
        if (spinner) {
          var spin = element('span', 'pm-cart-recs__spin');
          spin.setAttribute('aria-hidden', 'true');
          spin.append(spinner.content.cloneNode(true));
          fragment.append(spin);
        }
        fragment.append(this.dataset.addingLabel || 'Adding\u2026');
        return fragment;
      }

      // A stand-in for the line the add will create, put at the top of the list (the newest line leads) and
      // built from what the card already shows (and the option lines of the variant, from the product JSON). It
      // wears the real line's classes, so it lines up with the real lines; the repaint replaces it. Returns the
      // line, or null when there is no list to put it in.
      insertPending(button, inDrawer, options) {
        var card = button.closest('.pm-cart-recs__card');
        var scope = inDrawer ? this.closest('cart-drawer') : document.getElementById('main-cart-items');
        var list = scope && scope.querySelector('.pm-lines');
        if (!card || !list) return null;

        var line = element('li', 'cart-item pm-line pm-line--pending');
        line.setAttribute('aria-hidden', 'true');

        var media = element('div', 'cart-item__media pm-well pm-line__media');
        if (card.querySelector('.pm-well--cover')) media.classList.add('pm-well--cover');
        var photo = card.querySelector('.pm-cart-recs__media img');
        if (photo) {
          var img = element('img', 'pm-well__img');
          img.src = photo.currentSrc || photo.src;
          img.alt = '';
          media.append(img);
        }

        var main = element('div', 'pm-line__main');
        var title = card.querySelector('.pm-cart-recs__title');
        main.append(element('span', 'cart-item__name pm-line__name', title ? title.textContent.trim() : ''));
        if (options && options.length) {
          var opts = element('dl', 'pm-line__opts');
          options.forEach(function (option) {
            var row = document.createElement('div');
            // the space between the two is the whitespace of the real line's markup (both are display:inline)
            row.append(element('dt', '', option.name + ':'), ' ', element('dd', '', option.value));
            opts.append(row);
          });
          main.append(opts);
        }
        var adding = element('span', 'pm-line__adding');
        adding.append(this.addingContent());
        // not a control: it keeps the room Remove takes, so the row wraps (and the line is as tall) as the real one
        var room = element('span', 'pm-line__remove', 'Remove');
        room.setAttribute('aria-hidden', 'true');
        var row = element('div', 'cart-item__quantity-wrapper');
        row.append(adding, room);
        var stepper = element('div', 'pm-line__qty');
        stepper.append(row);
        main.append(stepper);

        var amount = card.querySelector('.pm-cart-recs__price');
        var prices = element('div', 'cart-item__price-wrapper pm-price');
        prices.append(element('span', 'pm-line__now', amount ? amount.textContent.trim() : ''));
        var cost = element('div', 'pm-line__price');
        cost.append(prices);

        line.append(media, main, cost);
        // WebKit has no scroll anchoring: the line is put in above the button, so the scroller is moved by the
        // same amount and the button stays under the shopper's finger
        var before = button.getBoundingClientRect().top;
        list.prepend(line);
        var shift = button.getBoundingClientRect().top - before;
        var scroller = inDrawer ? scope.querySelector('.pm-drawer__body') : null;
        if (shift) {
          if (scroller) scroller.scrollTop += shift;
          else if (!inDrawer) window.scrollBy(0, shift);
        }
        return line;
      }

      // `text` is Shopify's own sentence for a 422 ("only 3 available"). With null (a timeout, a network
      // error, a non-JSON answer, any other status) it is the product page's "couldn't confirm" sentence,
      // with "Check your cart" as a link. The status element is visible while it holds a failure (it is
      // visually hidden otherwise, for the success announcements); clearStatus hides it again.
      showAddFailure(text) {
        var status = this.querySelector('[data-pm-recs-status]');
        if (!status) return;
        status.classList.remove('visually-hidden');
        if (text !== null) {
          status.textContent = text;
          return;
        }
        var drawer = document.querySelector('cart-drawer');
        var strings = drawer ? drawer.dataset : {};
        var link = document.createElement('a');
        link.href = (window.routes && window.routes.cart_url) || '/cart';
        link.textContent = strings.checkCart || 'Check your cart';
        status.replaceChildren((strings.unconfirmed || "We couldn't confirm that.") + ' ', link);
      }

      // The add repainted the drawer or the cart page, which drops whatever had focus.
      // Put focus on the heading ("Your cart" in the drawer, the page title on /cart)
      // and, on /cart, say what happened through the cart's own live region.
      settleAfterAdd(name, inDrawer, announced) {
        var heading = inDrawer
          ? document.querySelector('cart-drawer .drawer__heading')
          : document.querySelector('cart-items .pm-cart__title');
        if (heading) {
          if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
          var drawerBox = inDrawer ? document.getElementById('CartDrawer') : null;
          // The drawer's focus trap still points at nodes the repaint removed:
          // set it again on the new ones, landing on the heading.
          if (drawerBox && typeof trapFocus === 'function') trapFocus(drawerBox, heading, { preventScroll: true });
          // on /cart the title is where the page is meant to scroll to: the new line is below it
          else heading.focus(inDrawer ? { preventScroll: true } : undefined);
        }

        // the drawer already said it, through its own region
        if (announced) return;

        var live = document.getElementById(inDrawer ? 'CartDrawer-LiveRegionText' : 'cart-live-region-text');
        var template = this.dataset.added || 'Added __PRODUCT__ to your cart';
        // a function, so a "$&" in a title stays text (as CartDrawer.addedText)
        var message = template.replace('__PRODUCT__', function () {
          return name || '';
        });
        if (!live) {
          // This element survives the add on /cart; in the drawer it was just replaced.
          live = this.isConnected ? this.querySelector('[data-pm-recs-status]') : null;
        }
        if (!live) return;
        live.removeAttribute('aria-hidden');
        live.textContent = '';
        // a region is only announced when its text changes after it is in the DOM
        setTimeout(function () {
          live.textContent = message;
        }, 100);
      }

      paintSections(sections, openDrawer) {
        if (!sections) return;

        var drawer = document.querySelector('cart-drawer');
        if (drawer && sections['cart-drawer']) {
          var nextDrawer = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer');
          var currentDrawer = document.getElementById('CartDrawer');
          if (nextDrawer && currentDrawer) currentDrawer.innerHTML = nextDrawer.innerHTML;
          drawer.classList.remove('is-empty');
          var overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
          // An open drawer is not opened again: open() parks a one-shot transitionend
          // listener that later pulls focus back onto the dialog box.
          if (openDrawer && typeof drawer.open === 'function' && !drawer.classList.contains('active')) drawer.open();
        }

        var icon = document.getElementById('cart-icon-bubble');
        if (icon && sections['cart-icon-bubble']) {
          var iconHtml = sectionInner(sections['cart-icon-bubble'], '.shopify-section');
          if (iconHtml) icon.innerHTML = iconHtml;
        }

        var itemsRoot = document.getElementById('main-cart-items');
        if (itemsRoot && itemsRoot.dataset.id && sections[itemsRoot.dataset.id]) {
          var itemsHtml = sectionInner(sections[itemsRoot.dataset.id], '.js-contents');
          var itemsSlot = itemsRoot.querySelector('.js-contents');
          if (itemsSlot && itemsHtml) itemsSlot.innerHTML = itemsHtml;
          var cartItems = document.querySelector('cart-items');
          if (cartItems) cartItems.classList.remove('is-empty');
        }

        var footer = document.getElementById('main-cart-footer');
        if (footer && footer.dataset.id && sections[footer.dataset.id]) {
          var footerHtml = sectionInner(sections[footer.dataset.id], '.js-contents');
          var footerSlot = footer.querySelector('.js-contents');
          if (footerSlot && footerHtml) footerSlot.innerHTML = footerHtml;
          footer.classList.remove('is-empty');
        }
      }
    }

    customElements.define('pm-cart-recs', PmCartRecs);
  }

  return {
    formatMoney: formatMoney,
    imageSrc: imageSrc,
    shortName: shortName,
    cardImage: cardImage,
    chooseCards: chooseCards,
    mount: mount,
  };
});
